import { useTranslation } from "react-i18next";
import styles from "./AcceptanceChart.module.css";

interface AcceptanceChartProps {
  acceptanceRates: { [category: string]: number };
  overallRate: number;
  totalSuggestions: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  grammar: styles.barGrammar,
  clarity: styles.barClarity,
  structure: styles.barStructure,
  style: styles.barStyle,
  logic: styles.barLogic,
};

export function AcceptanceChart({
  acceptanceRates,
  overallRate,
  totalSuggestions,
}: AcceptanceChartProps) {
  const { t } = useTranslation();
  const categories = Object.entries(acceptanceRates).sort(([, a], [, b]) => b - a);
  const hasData = totalSuggestions > 0;

  if (!hasData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t("voiceProfile.suggestionAcceptance")}</h3>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3v18h18" />
              <path d="M18 17V9" />
              <path d="M13 17V5" />
              <path d="M8 17v-3" />
            </svg>
          </div>
          <p className={styles.emptyText}>{t("voiceProfile.noSuggestionsYet")}</p>
          <p className={styles.emptyHint}>{t("voiceProfile.acceptanceHint")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t("voiceProfile.suggestionAcceptance")}</h3>
        <div className={styles.overallRate}>
          <span className={styles.overallLabel}>{t("voiceProfile.overallRate")}</span>
          <span className={styles.overallValue}>{Math.round(overallRate * 100)}%</span>
        </div>
      </div>

      <div className={styles.bars}>
        {categories.map(([category, rate]) => (
          <div key={category} className={styles.barRow}>
            <div className={styles.barHeader}>
              <span className={styles.categoryName}>{category}</span>
              <span className={styles.percentage}>{Math.round(rate * 100)}%</span>
            </div>
            <div className={styles.barTrack}>
              <div
                className={`${styles.barFill} ${CATEGORY_COLORS[category] || styles.barDefault}`}
                style={{ width: `${Math.round(rate * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {categories.length > 0 && (
        <div className={styles.legend}>
          {categories.map(([category]) => (
            <div key={category} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{
                  backgroundColor: getCategoryColor(category),
                }}
              />
              <span className={styles.legendLabel}>{category}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getCategoryColor(category: string): string {
  const colorMap: Record<string, string> = {
    grammar: "var(--color-suggest-grammar)",
    clarity: "var(--color-suggest-clarity)",
    structure: "var(--color-suggest-structure)",
    style: "var(--color-suggest-style)",
    logic: "var(--color-suggest-logic)",
  };
  return colorMap[category] || "var(--color-accent-light)";
}
